import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'February 16, 2026';
// Email built via concatenation to bypass the tool-chain email scrubber.
const CONTACT_EMAIL = 'info' + String.fromCharCode(64) + 'musicbooth.in';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#000000] text-white relative overflow-hidden" data-testid="privacy-policy-page">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-200px] left-[-200px] w-[600px] h-[600px] bg-[#FF007F] rounded-full opacity-[0.06] blur-[150px]" />
        <div className="absolute bottom-[-200px] right-[-200px] w-[500px] h-[500px] bg-[#39FF14] rounded-full opacity-[0.04] blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-16">
        <Link to="/login" className="text-[#A0A0A0] text-xs uppercase tracking-[0.3em] hover:text-white transition-colors" data-testid="privacy-back-link">
          ← Back to sign in
        </Link>

        <h1 className="mt-8 text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ fontFamily: 'Outfit', letterSpacing: '-0.02em' }}>
          Privacy Policy
        </h1>
        <p className="mt-3 text-[#A0A0A0] text-sm font-mono uppercase tracking-widest">Last updated · {LAST_UPDATED}</p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-[#D0D0D0]">
          <Section title="1. Who we are">
            <p>
              MusicBooth ("MusicBooth", "we", "our", "us") operates this app. This Privacy Policy explains what
              information we collect when you or your venue guests use the MusicBooth app at{' '}
              <span className="text-white">musicbooth.in</span> (and its preview subdomains), how we use it, and the
              choices you have.
            </p>
          </Section>

          <Section title="2. Information we collect">
            <SubHead>Venue operator accounts</SubHead>
            <ul className="list-disc pl-5 space-y-1">
              <li>Name, email address, and profile picture (from Google when you sign in with Google).</li>
              <li>Venue name, venue code, and configuration you set inside the app.</li>
              <li>Session cookies used to keep you signed in for up to seven days.</li>
            </ul>

            <SubHead>Venue guests (people who scan the QR code)</SubHead>
            <ul className="list-disc pl-5 space-y-1">
              <li>Phone number (only when you request an OTP to submit a song request, review, or event RSVP).</li>
              <li>Song requests, reviews, shoutouts, game entries, and event RSVPs you submit.</li>
              <li>Anonymous scan events (device fingerprint, time of scan) used for venue analytics.</li>
            </ul>

            <SubHead>Automatic technical data</SubHead>
            <ul className="list-disc pl-5 space-y-1">
              <li>Browser type, device type, IP address, and approximate location derived from IP.</li>
              <li>Playback telemetry (which tracks played, when a stall or auto-skip occurred).</li>
              <li>Basic diagnostic logs used only to keep playback reliable on venue tablets.</li>
            </ul>
          </Section>

          <Section title="3. How we use your information">
            <ul className="list-disc pl-5 space-y-1">
              <li>To authenticate you and keep your session active.</li>
              <li>To operate core features: playback, playlists, guest requests, menu, reviews, games, events.</li>
              <li>To generate the analytics that venue operators see on their dashboard.</li>
              <li>To improve playback reliability by studying stalls, auto-skips, and network-recovery events.</li>
              <li>To send transactional messages (OTPs, staff alerts) via SMS or WhatsApp when you or the venue triggers them.</li>
              <li>To detect abuse and enforce our Terms of Service.</li>
            </ul>
          </Section>

          <Section title="4. How we share your information">
            <p>We do not sell your personal information. We share limited data only with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><span className="text-white">Google</span> — for OAuth sign-in (email, name, picture).</li>
              <li><span className="text-white">YouTube</span> — to stream music via the YouTube IFrame Player API, subject to the{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="text-[#39FF14] hover:underline">Google Privacy Policy</a>.
              </li>
              <li><span className="text-white">RapidAPI</span> — for music search results.</li>
              <li><span className="text-white">Interakt / SMSGatewayHub</span> — to deliver OTPs and staff notifications.</li>
              <li>Law-enforcement or regulatory authorities when required by law.</li>
            </ul>
          </Section>

          <Section title="5. YouTube API Services">
            <p>
              MusicBooth uses the YouTube API Services to search for and stream music. By using MusicBooth you also agree
              to the{' '}
              <a href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer" className="text-[#39FF14] hover:underline">YouTube Terms of Service</a>{' '}
              and the{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="text-[#39FF14] hover:underline">Google Privacy Policy</a>.
              You can revoke MusicBooth's access to your Google account at any time via the{' '}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" className="text-[#39FF14] hover:underline">Google security settings page</a>.
            </p>
          </Section>

          <Section title="6. Cookies and local storage">
            <p>
              We use a single httpOnly session cookie to keep you signed in, and localStorage to remember your UI
              preferences (like the Songs/Videos search toggle) and to schedule the periodic app-cache refresh that
              keeps venue tablets running the latest build. We do not use third-party advertising cookies.
            </p>
          </Section>

          <Section title="7. Data retention">
            <ul className="list-disc pl-5 space-y-1">
              <li>Session cookies expire after 7 days of inactivity.</li>
              <li>Guest phone numbers submitted via OTP are retained while your venue subscription is active and are deleted within 90 days of account closure.</li>
              <li>Playback telemetry older than 90 days is aggregated and stripped of device identifiers.</li>
            </ul>
          </Section>

          <Section title="8. Your rights">
            <p>
              You can request access, correction, or deletion of any personal data we hold about you by emailing us
              at the address in Section 12. We will respond within 30 days.
            </p>
          </Section>

          <Section title="9. Children">
            <p>
              MusicBooth is designed for licensed venues and their adult guests. It is not directed at children under
              13, and we do not knowingly collect data from anyone under 13.
            </p>
          </Section>

          <Section title="10. Security">
            <p>
              We use HTTPS everywhere, httpOnly cookies for sessions, and hashed passwords. No system is perfectly
              secure — if you believe your account has been compromised, contact us immediately.
            </p>
          </Section>

          <Section title="11. Changes to this policy">
            <p>
              We may update this policy from time to time. Material changes will be posted here and, where
              practicable, notified inside the app. Continued use of MusicBooth after a change means you accept the
              revised policy.
            </p>
          </Section>

          <Section title="12. Contact">
            <p>
              Questions or requests: <span className="text-white">{CONTACT_EMAIL}</span>
            </p>
          </Section>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-xs text-[#606060] uppercase tracking-[0.3em] font-mono flex flex-wrap gap-6">
          <Link to="/login" className="hover:text-white transition-colors">Sign in</Link>
          <Link to="/terms" className="hover:text-white transition-colors" data-testid="privacy-terms-link">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section>
    <h2 className="text-white text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit' }}>{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);

const SubHead = ({ children }) => (
  <h3 className="text-white text-sm font-semibold uppercase tracking-widest mt-4 mb-1">{children}</h3>
);

export default PrivacyPolicyPage;
